import React, { useEffect, useMemo } from 'react'
import { snakeCase } from 'lodash-es'
import { FieldErrors, UseFormClearErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Combobox, Input } from '@node-space/ui-components'
import { useCountriesQuery } from 'hooks/queries/useCountriesQuery'
import { ContactFiatInstrumentFormData } from 'pages/Contacts/schemas/contactFiatInstrumentSchema'
import { AddressFields, AddressType, AddressTypes } from 'pages/Contacts/types/fiatInstruments'
import {
  BeneficiaryDetailsCurrency,
  BeneficiaryTransferDestination,
  USDBeneficiaryOptions,
} from 'types/beneficiaries'

type AddressFieldsProps = {
  errors: FieldErrors<ContactFiatInstrumentFormData>
  formValues: ContactFiatInstrumentFormData
  isInternationalBankAccount?: boolean
  type: AddressType
  clearErrors: UseFormClearErrors<ContactFiatInstrumentFormData>
  register: UseFormRegister<ContactFiatInstrumentFormData>
  setValue: UseFormSetValue<ContactFiatInstrumentFormData>
}

export const AddressDetails = ({
  errors,
  formValues,
  type,
  clearErrors,
  register,
  setValue,
}: AddressFieldsProps) => {
  const { t } = useTranslation()
  const { data: countries, isPending: countriesLoading } = useCountriesQuery(true)

  const isUsd = formValues?.currency === BeneficiaryDetailsCurrency.USD
  const isSwift = formValues?.transferDestination === BeneficiaryTransferDestination.INTERNATIONAL
  const isUsdLocalAndNotCubix = isUsd && formValues?.paymentMethod !== USDBeneficiaryOptions.CUBIX
  const isOptional = (!isUsdLocalAndNotCubix && !isSwift) || (!isUsd && !isSwift)
  const isBeneficiaryAddressDetails = type === AddressTypes.BENEFICIARY

  const labels = {
    addressLine1: t('formFields.addressLine1'),
    addressLine2: t('formFields.addressLine2'),
    city: t('formFields.city'),
    postCode: t('formFields.postCode'),
    region: t('formFields.region'),
    country: t('formFields.country'),
  }

  useEffect(() => {
    clearErrors()
  }, [formValues?.currency, formValues?.paymentMethod, clearErrors])

  const handleInputChange =
    (field: keyof ContactFiatInstrumentFormData) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setValue(field, event.target.value, {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

  const countryOptions = useMemo(() => {
    if (countries?.length) {
      return countries?.map(country => ({
        leftText: country?.name,
        value: country?.code,
      }))
    }

    return []
  }, [countries])

  const handleCountrySelect = (countryCode: string) => {
    const countryName = countryOptions.find(country => country?.value === countryCode)?.leftText
    setValue(getAddressFieldName('CountryName'), countryName)
    setValue(getAddressFieldName('CountryCode'), countryCode)
  }

  const getAddressFieldName = (baseName: AddressFields) => {
    return `${type}${baseName}` as keyof ContactFiatInstrumentFormData
  }

  return (
    <Box width="full">
      <Box flexCol width="full" gapX={12} gapY={16}>
        <Input
          {...register(getAddressFieldName('AddressLine1'))}
          name={getAddressFieldName('AddressLine1')}
          testId={snakeCase(getAddressFieldName('AddressLine1')?.toLowerCase())}
          label={labels?.addressLine1}
          placeholder={t('inputPlaceholders.enterAddressLine1Placeholder')}
          errorText={errors?.[getAddressFieldName('AddressLine1')]?.message}
          error={!!errors?.[getAddressFieldName('AddressLine1')]}
          onChange={handleInputChange(getAddressFieldName('AddressLine1'))}
          optionalText={isOptional && t('optional')}
        />
        <Input
          {...register(getAddressFieldName('AddressLine2'))}
          name={getAddressFieldName('AddressLine2')}
          testId={snakeCase(getAddressFieldName('AddressLine2')?.toLowerCase())}
          label={labels?.addressLine2}
          placeholder={t('inputPlaceholders.enterAddressLine2Placeholder')}
          errorText={errors?.[getAddressFieldName('AddressLine2')]?.message}
          error={!!errors?.[getAddressFieldName('AddressLine2')]}
          onChange={handleInputChange(getAddressFieldName('AddressLine2'))}
          optionalText={t('optional')}
        />

        <Box flex gapX={12}>
          <Box flexSize="auto" width="full">
            <Input
              {...register(getAddressFieldName('City'))}
              name={getAddressFieldName('City')}
              testId={snakeCase(getAddressFieldName('City')?.toLowerCase())}
              label={labels?.city}
              placeholder={t('inputPlaceholders.enterCityPlaceholder')}
              errorText={errors?.[getAddressFieldName('City')]?.message}
              error={!!errors?.[getAddressFieldName('City')]}
              onChange={handleInputChange(getAddressFieldName('City'))}
              optionalText={isOptional && t('optional')}
            />
          </Box>
          <Box flexSize="auto" width="full">
            <Input
              {...register(getAddressFieldName('PostCode'))}
              name={getAddressFieldName('PostCode')}
              label={labels?.postCode}
              testId={snakeCase(getAddressFieldName('PostCode')?.toLowerCase())}
              placeholder={t('inputPlaceholders.enterPostalCodePlaceholder')}
              errorText={errors?.[getAddressFieldName('PostCode')]?.message}
              error={!!errors?.[getAddressFieldName('PostCode')]}
              onChange={handleInputChange(getAddressFieldName('PostCode'))}
              optionalText={isOptional && t('optional')}
            />
          </Box>
        </Box>

        <Input
          {...register(getAddressFieldName('Region'))}
          name={getAddressFieldName('Region')}
          label={labels?.region}
          testId={snakeCase(getAddressFieldName('Region')?.toLowerCase())}
          placeholder={t('inputPlaceholders.enterRegionPlaceholder')}
          errorText={errors?.[getAddressFieldName('Region')]?.message}
          error={!!errors?.[getAddressFieldName('Region')]}
          onChange={handleInputChange(getAddressFieldName('Region'))}
          optionalText={isOptional && t('optional')}
        />
      </Box>
      {isSwift && isBeneficiaryAddressDetails && (
        <Box paddingT={12}>
          <Combobox
            {...register(getAddressFieldName('CountryCode'))}
            label={t('country')}
            id={snakeCase(getAddressFieldName('CountryCode')?.toLowerCase())}
            selectPlaceholder={t('searchOrSelectACountry')}
            items={countryOptions}
            onChange={handleCountrySelect}
            loading={countriesLoading}
            defaultValue={formValues?.[getAddressFieldName('CountryCode')] as string}
            errorText={errors?.[getAddressFieldName('CountryCode')]?.message}
            optionalText={isOptional && t('optional')}
          />
        </Box>
      )}
    </Box>
  )
}
